import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Typography, Form, Select, Rate, Input, Button,
  message, Spin, Empty, List, Tag, Popconfirm, Result
} from 'antd';
import { MessageOutlined, StarOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { supabase, getCurrentOrgId, formatDate } from '@ubus/shared';
import type { Trip } from '@ubus/shared';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Feedback {
  id: string;
  trip_id: string;
  rating: number;
  category: string;
  message: string;
  status: string;
  created_at: string;
}

const FEEDBACK_CATEGORY_KEYS = [
  'driver',
  'vehicle',
  'punctuality',
  'communication',
  'safety',
  'other',
] as const;

function TripFeedback() {
  const { t } = useTranslation();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form] = Form.useForm();

  const categoryOptions = useMemo(
    () =>
      FEEDBACK_CATEGORY_KEYS.map((key) => ({
        value: key,
        label: t(`feedback.categories.${key}`),
      })),
    [t]
  );

  useEffect(() => {
    getCurrentOrgId().then(setOrgId);
  }, []);

  const loadData = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { data: recentTrips } = await supabase
        .from('trips')
        .select('*')
        .eq('org_id', orgId)
        .eq('status', 'completed')
        .gte('date', dayjs().subtract(30, 'day').format('YYYY-MM-DD'))
        .order('date', { ascending: false })
        .limit(20);

      setTrips(recentTrips || []);

      const { data: existingFeedback, error } = await supabase
        .from('trip_feedback')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });

      if (error) {
        message.error(error.message);
      } else {
        setFeedbacks(existingFeedback || []);
      }
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const categoryLabel = (value: string) =>
    categoryOptions.find((c) => c.value === value)?.label ?? value;

  const handleSubmit = async (values: { trip_id: string; category: string; rating: number; message: string }) => {
    if (!orgId) return;

    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        message.error(t('feedback.loginRequired'));
        return;
      }

      const { error } = await supabase.from('trip_feedback').insert({
        trip_id: values.trip_id,
        org_id: orgId,
        reporter_id: user.id,
        rating: values.rating || 5,
        category: values.category || 'other',
        message: values.message,
        status: 'new'
      });

      if (error) {
        message.error(error.message);
        return;
      }

      setSubmitted(true);
      message.success(t('feedback.thankYou'));
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('feedback.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (feedbackId: string) => {
    const { error } = await supabase.from('trip_feedback').delete().eq('id', feedbackId);
    if (error) {
      message.error(error.message);
      return;
    }
    setFeedbacks(prev => prev.filter(f => f.id !== feedbackId));
    message.success(t('feedback.deleted'));
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;
  }

  return (
    <div>
      <div className="client-page-header">
        <div>
          <Title level={3} className="client-page-title">{t('feedback.title')}</Title>
          <Text className="client-page-subtitle">
            {t('feedback.subtitle')}
          </Text>
        </div>
      </div>

      <Card
        title={
          <span>
            <StarOutlined style={{ marginRight: 8, color: '#d97706' }} />
            {t('feedback.submitTitle')}
          </span>
        }
        style={{ borderRadius: 12, marginBottom: 24, borderColor: 'var(--client-border)' }}
      >
        {submitted ? (
          <Result
            status="success"
            title={t('feedback.submittedTitle')}
            subTitle={t('feedback.submittedSubtitle')}
            extra={
              <Button
                type="primary"
                onClick={() => {
                  form.resetFields();
                  setSubmitted(false);
                  void loadData();
                }}
                icon={<ReloadOutlined />}
              >
                {t('feedback.submitAnother')}
              </Button>
            }
          />
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ rating: 5 }}>
            <Form.Item name="trip_id" label={t('feedback.tripLabel')} rules={[{ required: true }]}>
              <Select
                showSearch
                placeholder={t('feedback.selectTrip')}
                options={trips.map((trip) => ({
                  value: trip.id,
                  label: `${formatDate(trip.date)} — ${trip.pickup_address} (${trip.passenger_count} ${t('common.passengers').toLowerCase()})`,
                }))}
              />
            </Form.Item>
            <Form.Item name="category" label={t('feedback.categoryLabel')} rules={[{ required: true }]}>
              <Select options={categoryOptions} />
            </Form.Item>
            <Form.Item name="rating" label={t('feedback.ratingLabel')}>
              <Rate allowHalf style={{ fontSize: 28, color: '#d97706' }} />
            </Form.Item>
            <Form.Item name="message" label={t('feedback.commentsLabel')} rules={[{ required: true }]}>
              <TextArea rows={4} maxLength={1000} showCount placeholder={t('feedback.commentsPlaceholder')} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting} icon={<MessageOutlined />}>
                {t('feedback.submitButton')}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>

      {feedbacks.length > 0 && (
        <Card title={t('feedback.previousTitle')} style={{ borderRadius: 12, borderColor: 'var(--client-border)' }}>
          <List
            dataSource={feedbacks}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Popconfirm
                    key="delete"
                    title={t('feedback.deleteConfirm')}
                    okText={t('common.delete')}
                    cancelText={t('common.cancel')}
                    onConfirm={() => handleDelete(item.id)}
                  >
                    <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  title={
                    <span>
                      <Tag color="blue">{categoryLabel(item.category)}</Tag>
                      <Rate disabled value={item.rating} style={{ fontSize: 14 }} />
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{formatDate(item.created_at)}</Text>
                    </span>
                  }
                  description={item.message}
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {feedbacks.length === 0 && !submitted && <Empty description={t('feedback.noFeedbackYet')} />}
    </div>
  );
}

export default TripFeedback;
